import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { toast } from "@babackapp/ui/components/sonner";
import { format } from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";

type SizingExportButtonProps = {
  fromTimestamp?: number;
  toTimestamp?: number;
};

export function SizingExportButton({
  fromTimestamp,
  toTimestamp,
}: SizingExportButtonProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await fetchClient.GET(
        "/shops/{shopId}/sizing/diagnostic",
        {
          params: {
            path: { shopId: shop.id },
            query: { fromTimestamp, toTimestamp },
          },
        },
      );

      if (error || !data) {
        toast.error(t("misc.error"));
        return;
      }

      const rows = data.map((item) => ({
        productId: item.productId,
        product: item.productTitle,
        recommendation: item.recommendation ?? "",
        sizeTooSmallCount: item.sizeTooSmallCount ?? 0,
        sizeTooLargeCount: item.sizeTooLargeCount ?? 0,
        sampleSize: item.sampleSize ?? 0,
      }));

      if (rows.length === 0) {
        toast.info(t("misc.noResultFound"));
        return;
      }

      const escape = (value: unknown) => {
        if (value == null) return "";
        const str = value.toString();
        if (/[";,\n]/.test(str)) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const keys = [
        "productId",
        "product",
        "recommendation",
        "sizeTooSmallCount",
        "sizeTooLargeCount",
        "sampleSize",
      ] as const;
      const header = keys.join(";");
      const csvRows = rows
        .map((row) => keys.map((key) => escape(row[key])).join(";"))
        .join("\n");

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + header + "\n" + csvRows], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sizing-${format(new Date(), "yyyy-MM-dd")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Sizing CSV export failed", err);
      toast.error(t("misc.error"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          disabled={isExporting}
          aria-label={t("sizing.export.all")}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportCsv}>
          {t("sizing.export.all")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
